import Spinner from "@/components/spinner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useCreateCompany } from "@/hooks/create-company.hook";
import { validationEmail } from "@/lib/utils";
import { LampCharge } from "iconsax-react";
import React, { FC, useState } from "react";

interface MainContactI {
  name: string;
  lastname: string;
  email: string;
  phone: string;
}

interface Props {
  mainContact: MainContactI;
  setMainContact: React.Dispatch<React.SetStateAction<MainContactI>>;
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
  name: string;
  setName: React.Dispatch<React.SetStateAction<string>>;
  ruc: string;
  setRuc: React.Dispatch<React.SetStateAction<string>>;
  tradeName: string;
  setTradeName: React.Dispatch<React.SetStateAction<string>>;
  address: string;
  setAddress: React.Dispatch<React.SetStateAction<string>>;
  website: string;
  setWebsite: React.Dispatch<React.SetStateAction<string>>;
  token: string | null;
}

const MainContact: FC<Props> = ({
  mainContact,
  setCurrentStep,
  setMainContact,
  address,
  name,
  ruc,
  tradeName,
  website,
  token,
}) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [formError, setFormError] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const { mutate: createCompanyMutation } = useCreateCompany();

  const goBack = () => {
    setCurrentStep(0);
    setFormError({});
  };

  const handleRegisterCompany = () => {
    setLoading(true);
    const companyData = {
      token: token as string,
      taxId: ruc,
      name,
      tradeName,
      website,
      address: {
        street: address,
      },
      mainContact: {
        names: `${mainContact.name} ${mainContact.lastname}`,
        email: mainContact.email,
      },
    };
    createCompanyMutation(companyData, {
      onSuccess: (res) => {
        console.log({ res });
        setCurrentStep(2);
        setLoading(false);
      },
      onError: (error) => {
        console.error(error);
        setLoading(false);
      },
    });
  };

  const handleClick = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const errors: any = {};
    if (!mainContact.name) {
      errors.name = true;
      errors.nameMessage = "El nombre es obligatorio";
    } else if (!mainContact.lastname) {
      errors.lastname = true;
      errors.lastnameMessage = "El apellido es obligatorio";
    } else if (!mainContact.email) {
      errors.email = true;
      errors.emailMessage = "El correo es obligatorio";
    } else if (!validationEmail(mainContact.email)) {
      errors.email = true;
      errors.emailMessage = "Ingresa un correo válido";
    } else {
      handleRegisterCompany();
    }
    setFormError(errors);
  };

  return (
    <div>
      <div className="mb-4 mt-10 rounded-lg border border-[#615CB7] px-4 py-5">
        <div className="flex items-center gap-2">
          <LampCharge size="22" color="#615CB7" />
          <p className="flex-1 text-base">Recuerda que...</p>
        </div>
        <div className="flex items-center gap-2">
          <LampCharge size="22" color="transparent" />
          <p className="mt-2 flex-1 text-sm">
            Esta persona tendrá el perfil de Responsable, con la capacidad de
            administrar usuarios adicionales, como Colaboradores o Empresas. Una
            vez que la empresa esté creada y aprobada, se podrán añadir tantos
            usuarios como sean necesarios.
          </p>
        </div>
      </div>
      <div className="flex flex-col items-center justify-center">
        <div className="flex w-full max-w-[450px] flex-col gap-3">
          <p className="mt-3 text-sm font-medium text-[#4F5D6D]">Nombre*</p>
          <Input
            value={mainContact.name}
            placeholder="Ingresa el nombre"
            className={`rounded-2xl border-[#A0ACBA] py-6 ${
              formError.name ? "border-red-500" : ""
            }`}
            onChange={(event) => {
              setFormError({
                ...formError,
                name: false,
              });
              setMainContact({ ...mainContact, name: event.target.value });
            }}
          />
          {formError.name && formError.nameMessage && (
            <p className="mt-1 text-xs text-red-500">
              {formError?.nameMessage}
            </p>
          )}
          <p className="mt-3 text-sm font-medium text-[#4F5D6D]">Apellidos*</p>
          <Input
            value={mainContact.lastname}
            placeholder="Ingresa el apellido"
            className={`rounded-2xl border-[#A0ACBA] py-6 ${
              formError.lastname ? "border-red-500" : ""
            }`}
            onChange={(event) => {
              setFormError({
                ...formError,
                lastname: false,
              });
              setMainContact({ ...mainContact, lastname: event.target.value });
            }}
          />
          {formError.lastname && formError.lastnameMessage && (
            <p className="mt-1 text-xs text-red-500">
              {formError?.lastnameMessage}
            </p>
          )}
          <p className="mt-3 text-sm font-medium text-[#4F5D6D]">
            Correo electrónico*
          </p>
          <Input
            value={mainContact.email}
            placeholder="Ingresa el correo"
            className={`rounded-2xl border-[#A0ACBA] py-6 ${
              formError.email ? "border-red-500" : ""
            }`}
            onChange={(event) => {
              setFormError({
                ...formError,
                email: false,
              });
              setMainContact({
                ...mainContact,
                email: event.target.value.trim(),
              });
            }}
          />
          {formError.email && formError.emailMessage && (
            <p className="mt-1 text-xs text-red-500">
              {formError?.emailMessage}
            </p>
          )}
        </div>
        <div className="mb-3 mt-8 flex gap-3">
          <Button
            disabled={loading}
            type="button"
            variant="secondary"
            className="color-[#293038] h-12 w-[200px] select-none rounded-xl border border-[#A0ACBA] font-medium"
            onClick={goBack}
          >
            Anterior
          </Button>
          <Button
            type="button"
            className="color-[#293038] h-12 w-[200px] select-none rounded-xl border border-[#A0ACBA] bg-[#262452] font-medium text-white"
            onClick={handleClick}
          >
            {loading ? <Spinner className="mr-2" /> : <>Siguiente</>}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MainContact;
